import { EraseButton } from '../components/EraseButton';
import { useState } from 'react';
import { ApiError } from '../lib/api';
import { useI18n, useLocalized, useOtherLanguages } from '../lib/i18n';
import { useApiError } from '../lib/useApiError';
import { formatGel, formatRate } from '../lib/format';
import { parseMoneyToTetri, tetriToInput } from '../lib/money';
import { useServices, useUpdateService } from '../lib/queries';
import { can, useSession } from '../lib/session';
import type { Service } from '../lib/types';
import { NewService } from '../components/NewService';
import {
  Alert, Button, Card, Label, MoneyInput, Pill, SectionTitle, Skeleton,
} from '../components/ui';

function ServiceRow({ service, mayEdit, maySeePay }: {
  service: Service; mayEdit: boolean; maySeePay: boolean;
}) {
  const localized = useLocalized();
  const otherLanguages = useOtherLanguages();
  const { t } = useI18n();
  const update = useUpdateService();
  const [open, setOpen] = useState(false);
  const [priceText, setPriceText] = useState(
    service.priceFromTetri === null ? '' : tetriToInput(service.priceFromTetri),
  );
  const [payText, setPayText] = useState(
    service.staffPayTetri === null ? '' : tetriToInput(service.staffPayTetri),
  );

  const errorText = useApiError();
  const error = update.error instanceof ApiError ? update.error : null;

  return (
    <Card className={service.isActive ? '' : 'opacity-60'}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-ink">{localized(service.name)}</span>
            {!service.isActive && <Pill tone="warning">{t('services.hiddenPill')}</Pill>}
            {!service.cashbackEnabled && <Pill>{t('services.noCashbackPill')}</Pill>}
            {!service.pointsEnabled && <Pill>{t('services.noPointsPill')}</Pill>}
            {service.cashbackRateBpOverride !== null && (
              <Pill tone="warning">
                {t('services.overridePill', { rate: formatRate(service.cashbackRateBpOverride) })}
              </Pill>
            )}
          </div>
          <div className="mt-1 text-xs text-ink-faint">
            {otherLanguages(service.name)}
          </div>
          <div className="mt-1 text-[0.68rem] text-ink-faint">
            {service.followupDays
              ? t('services.followupAfter', { days: service.followupDays })
              : t('services.noFollowup')}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <div className="text-right">
            <div className="tabular text-sm font-semibold text-ink">
              {service.priceFromTetri === null
                ? '—'
                : t('services.priceFrom', { price: formatGel(service.priceFromTetri) })}
            </div>
            <div className="text-[0.6rem] uppercase tracking-wider text-ink-faint">{t('services.startingPrice')}</div>
            {maySeePay && service.staffPayTetri !== null && (
              <div className="tabular mt-1 text-[0.7rem] text-ink-dim">
                {t('services.staffPayShort', { amount: formatGel(service.staffPayTetri) })}
              </div>
            )}
          </div>
          {mayEdit && (
            <Button variant="quiet" size="sm" onClick={() => setOpen((v) => !v)}>
              {open ? t('staff.close') : t('common.edit')}
            </Button>
          )}
        </div>
      </div>

      {open && (
        <div className="mt-3 space-y-3 border-t border-line pt-3">
          {error && <Alert>{errorText(error) ?? error.message}</Alert>}

          <div className="grid gap-3 sm:grid-cols-2">
            <MoneyInput
              label={t('services.startingPrice')}
              placeholder={t('services.nonePublished')}
              value={priceText}
              onChange={(event) => setPriceText(event.target.value)}
            />
            {maySeePay && (
              <MoneyInput
                label={t('services.staffPay')}
                placeholder={t('services.staffPayNone')}
                value={payText}
                onChange={(event) => setPayText(event.target.value)}
              />
            )}
            <div className="flex flex-col gap-1.5">
              <Label>{t('services.earns')}</Label>
              <div className="flex gap-2">
                {([
                  { key: 'cashbackEnabled' as const, label: 'services.cashback' as const },
                  { key: 'pointsEnabled' as const, label: 'services.points' as const },
                ]).map((toggle) => (
                  <button
                    key={toggle.key}
                    onClick={() => update.mutate({ id: service.id, [toggle.key]: !service[toggle.key] })}
                    className={`min-h-11 flex-1 rounded-xl border text-sm font-medium transition-colors ${
                      service[toggle.key]
                        ? 'border-brand bg-brand-wash text-brand'
                        : 'border-line bg-raised text-ink-faint'
                    }`}
                  >
                    {t(toggle.label)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              loading={update.isPending}
              onClick={() => update.mutate({
                id: service.id,
                priceFromTetri: priceText.trim() === '' ? null : parseMoneyToTetri(priceText),
                // Only sent by someone allowed to set pay; the server refuses it otherwise.
                ...(maySeePay
                  ? { staffPayTetri: payText.trim() === '' ? null : parseMoneyToTetri(payText) }
                  : {}),
              }, { onSuccess: () => setOpen(false) })}
            >{t('common.save')}</Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => update.mutate({ id: service.id, isActive: !service.isActive })}
            >
              {service.isActive ? t('services.hideFromTill') : t('services.showInTill')}
            </Button>
            <EraseButton compact path={`services/${service.id}`} name={localized(service.name)} />
          </div>

        </div>
      )}
    </Card>
  );
}

export function ServicesPage() {
  const { t } = useI18n();
  const localized = useLocalized();
  const { data: session } = useSession();
  const { data, isPending } = useServices();
  const mayEdit = can(session, 'services.update');
  const maySeePay = can(session, 'staff.pay');
  const mayCreate = can(session, 'services.create');
  const [adding, setAdding] = useState(false);

  const byCategory = new Map<string, Service[]>();
  for (const service of data?.items ?? []) {
    byCategory.set(service.categorySlug, [...(byCategory.get(service.categorySlug) ?? []), service]);
  }

  return (
    <div className="mx-auto max-w-4xl">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-[1.75rem] font-bold leading-tight tracking-tight text-ink sm:text-[2rem]">{t('services.title')}</h1>
        </div>
        {mayCreate && !adding && (
          <Button onClick={() => setAdding(true)}>{t('services.new')}</Button>
        )}
      </header>

      {adding && <div className="mb-5"><NewService onDone={() => setAdding(false)} /></div>}

      {isPending && (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}</div>
      )}

      <div className="space-y-5">
        {[...byCategory.entries()].map(([slug, services]) => (
          <section key={slug}>
            <SectionTitle>
              {services[0] ? localized(services[0].categoryName) : slug}
            </SectionTitle>
            <div className="space-y-2">
              {services.map((service) => (
                <ServiceRow key={service.id} service={service} mayEdit={mayEdit} maySeePay={maySeePay} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
